import { PlusIcon } from '@acceso/design-system';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DECLARATION_FORM_DEFAULT_VALUES } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitDeclarations/constants';
import { TBroadcastScheduleProgramBodySegmentToolkitDeclarationForm } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitDeclarations/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePatchClipDeclarations } from '@/features/clips/api/patch-clips-declaration';
import { usePostClipDeclaration } from '@/features/clips/api/post-clips-declaration';
import { TPatchClipDeclaration, TPostClipDeclaration } from '@/features/clips/types';
import { usePostFeaturedPeople } from '@/features/featured-people/api/post-featured-people';
import { useFeaturedPeoplePermissions } from '@/features/featured-people/hooks/useFeaturedPeoplePermissions';
import { useSaveDeclarantAsPeople } from '@/features/featured-people/hooks/useSaveDeclarantAsPeople';
import { TFeaturedPeopleForm, TFeaturedPeopleType } from '@/features/featured-people/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';

export function useClipDeclarationFormState() {
  const {
    declarationClipData,
    formBox,
    activeClip,
    setLogoData,
    setFormBox,
    setActiveClip,
    drawnFormReset,
    setClipItems,
  } = useBroadcastScheduleProgram();
  const postClipDeclaration = usePostClipDeclaration();
  const patchClipDeclaration = usePatchClipDeclarations();
  const [newPerson, setNewPerson] = useState(false);

  const form = useForm<TBroadcastScheduleProgramBodySegmentToolkitDeclarationForm>({
    mode: 'all',
    defaultValues: DECLARATION_FORM_DEFAULT_VALUES,
  });
  const peopleDrawer = useDisclosureWithData();
  const { t } = useTranslation();
  const { mutateAsync: createPeople, isPending: isCreatingPeople } = usePostFeaturedPeople();
  const { canCreate: canCreatePeople } = useFeaturedPeoplePermissions();
  const { linkDeclarant, isSaving: isSavingPeople } = useSaveDeclarantAsPeople();
  const [savePersonAsPeople, setSavePersonAsPeople] = useState(false);

  const id = form.watch('id');

  useEffect(() => {
    setSavePersonAsPeople(false);

    if (!newPerson) {
      form.clearErrors('people_id');
      form.setValue('entity_label', undefined);
    } else {
      form.clearErrors('entity_label');
      form.setValue('people_id', null);
    }
  }, [newPerson]);

  const renderAdditionalButtons = () => {
    return [
      {
        label: t('Crear nueva persona de interés'),
        icon: PlusIcon,
        onClick: () => peopleDrawer.open(),
      },
    ];
  };

  const handleSubmitPeople = useCallback(
    async (data: TFeaturedPeopleForm): Promise<TFeaturedPeopleType> => {
      try {
        const result = await createPeople(data);
        peopleDrawer.close();
        return result;
      } catch (error) {
        console.error('Error during mutation', error);
        throw error;
      }
    },
    [createPeople]
  );

  const handleSubmit = async (data: TBroadcastScheduleProgramBodySegmentToolkitDeclarationForm) => {
    const hasPersonId = Boolean(data.people_id);
    const hasEntityLabel = Boolean(data.entity_label);

    if (!newPerson) {
      if (!hasPersonId) {
        form.setError('people_id', {
          type: 'manual',
          message: t('Seleccione una persona'),
        });
        return;
      }
    } else {
      if (!hasEntityLabel) {
        form.setError('entity_label', {
          type: 'manual',
          message: t('El nombre de la persona es obligatorio'),
        });
        return;
      }
    }

    // Abort the declaration when the declarant could not be saved: persisting it
    // anyway would silently fall back to an orphaned record.
    const declarationData = await linkDeclarant(data, savePersonAsPeople);
    if (!declarationData) return;

    const formatedData: TPostClipDeclaration = {
      ...declarationData,
      entity_label: declarationData.entity_label || null,
      clip_id: activeClip?.id || '',
      end_datetime: declarationData.start_datetime,
    };
    if (id) {
      handleUpdate(formatedData);
    } else {
      handleCreate(formatedData);
    }
  };

  const handleCreate = async (formatedData: TPostClipDeclaration) => {
    try {
      const item = await postClipDeclaration.mutateAsync(formatedData);
      setActiveClip(prev => {
        if (prev) {
          return {
            ...prev,
            clip_declarations: [...(prev.clip_declarations || []), item],
          };
        }
        return prev;
      });
      setClipItems(prev =>
        prev.map(clip => {
          if (clip.id === formatedData?.clip_id) {
            return { ...clip, clip_declarations: [...(clip.clip_declarations || []), item] };
          }
          return clip;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al crear el people:', error);
    }
  };

  const handleUpdate = async (formatedData: TPostClipDeclaration) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { clip_id, ...restData } = formatedData;
    const body: TPatchClipDeclaration = restData;
    try {
      const item = await patchClipDeclaration.mutateAsync({
        id,
        body,
      });
      setActiveClip(prev => {
        if (prev) {
          return {
            ...prev,
            clip_declarations: prev.clip_declarations?.map(declaration => {
              if (declaration.id === item.id) {
                return item;
              }
              return declaration;
            }),
          };
        }
        return prev;
      });
      setClipItems(prev =>
        prev.map(clip => {
          if (clip.id === formatedData?.clip_id) {
            return {
              ...clip,
              clip_declarations: (clip.clip_declarations || []).map(ocr => {
                if (ocr.id === item.id) {
                  return item;
                }
                return ocr;
              }),
            };
          }
          return clip;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al actualizar la persona de interés:', error);
    }
  };

  const handleClickExit = useCallback(() => {
    form.reset(DECLARATION_FORM_DEFAULT_VALUES);
    drawnFormReset();
  }, [setFormBox, setLogoData]);

  useEffect(() => {
    if (formBox !== 'declaration') return;

    if (declarationClipData?.id) {
      form.reset({
        id: declarationClipData.id,
        text: declarationClipData.text,
        people_id: declarationClipData.people_id,
        start_datetime: declarationClipData.start_datetime
          ? new Date(declarationClipData.start_datetime)
          : null,
        entity_label: declarationClipData.entity_label,
      });
    }
  }, [declarationClipData]);

  return {
    form,
    newPerson,
    setNewPerson,
    isCreatingPeople,
    peopleDrawer,
    canCreatePeople,
    savePersonAsPeople,
    setSavePersonAsPeople,
    isSavingPeople,
    renderAdditionalButtons,
    handleSubmitPeople,
    handleSubmit,
    handleClickExit,
  };
}
